import { useState, useEffect } from 'react';
import { Select, Spin, Alert } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import PageLayout from '@components/layout/PageLayout';
import { NSPButton } from '@components/ui/NSPButton';
import useSession from '@hooks/useSession';
import { GET_OATT_PEAK_CONFIG, UPSERT_OATT_PEAK_CONFIG } from '@graphql/oattPeakConfig.gql';
import styles from './OattPeakConfig.module.css';

const { Option } = Select;

// ─── Constants ────────────────────────────────────────────────────────────────

const DAY_OPTIONS = [
    { label: 'Mon', value: 1 },
    { label: 'Tue', value: 2 },
    { label: 'Wed', value: 3 },
    { label: 'Thu', value: 4 },
    { label: 'Fri', value: 5 },
    { label: 'Sat', value: 6 },
    { label: 'Sun', value: 7 },
];

const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => String(i).padStart(2, '0'));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

function splitTime(t: string | null | undefined): [string, string] {
    if (!t) return ['00', '00'];
    const [h = '00', m = '00'] = t.split(':');
    return [h, m];
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PeakConfig {
    on_peak_day_start: number;
    on_peak_day_end: number;
    off_peak_day_start: number;
    off_peak_day_end: number;
    on_peak_start: string;
    on_peak_end: string;
    off_peak_start: string;
    off_peak_end: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

const OattPeakConfigPage = () => {
    const { isOattRateOwner } = useSession();
    // Only the OATT Rate Owner may edit — Preparer and Approver get read-only access.
    const isReadOnly = !isOattRateOwner;

    const { data, loading, error } = useQuery<{ getOattPeakConfig: PeakConfig | null }>(
        GET_OATT_PEAK_CONFIG,
        { fetchPolicy: 'cache-and-network' }
    );

    const [upsertConfig, { loading: saving }] = useMutation(UPSERT_OATT_PEAK_CONFIG, {
        refetchQueries: [{ query: GET_OATT_PEAK_CONFIG }],
        awaitRefetchQueries: true,
    });

    // ── Form state (defaults match seeded DB row) ──
    const [onPeakDayStart, setOnPeakDayStart] = useState<number>(1);
    const [onPeakDayEnd, setOnPeakDayEnd] = useState<number>(5);
    const [offPeakDayStart, setOffPeakDayStart] = useState<number>(6);
    const [offPeakDayEnd, setOffPeakDayEnd] = useState<number>(7);
    const [onPeakStart, setOnPeakStart] = useState<string>('08:00');
    const [onPeakEnd, setOnPeakEnd] = useState<string>('24:00');
    const [offPeakStart, setOffPeakStart] = useState<string>('00:00');
    const [offPeakEnd, setOffPeakEnd] = useState<string>('08:00');
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');
    const [saveError, setSaveError] = useState<string>('');

    // ── Seed from DB ──
    useEffect(() => {
        const cfg = data?.getOattPeakConfig;
        if (!cfg) return;
        setOnPeakDayStart(cfg.on_peak_day_start);
        setOnPeakDayEnd(cfg.on_peak_day_end);
        setOffPeakDayStart(cfg.off_peak_day_start);
        setOffPeakDayEnd(cfg.off_peak_day_end);
        setOnPeakStart(cfg.on_peak_start);
        setOnPeakEnd(cfg.on_peak_end);
        setOffPeakStart(cfg.off_peak_start);
        setOffPeakEnd(cfg.off_peak_end);
    }, [data]);

    // ── Save ──
    const handleSave = async () => {
        setSaveStatus('idle');
        try {
            await upsertConfig({
                variables: {
                    input: {
                        on_peak_day_start: onPeakDayStart,
                        on_peak_day_end: onPeakDayEnd,
                        off_peak_day_start: offPeakDayStart,
                        off_peak_day_end: offPeakDayEnd,
                        on_peak_start: onPeakStart,
                        on_peak_end: onPeakEnd,
                        off_peak_start: offPeakStart,
                        off_peak_end: offPeakEnd,
                    },
                },
            });
            setSaveStatus('saved');
        } catch (err: unknown) {
            setSaveStatus('error');
            setSaveError(err instanceof Error ? err.message : 'Save failed');
        }
    };

    const change = () => setSaveStatus('idle');

    // ── Render ──
    if (loading) return <PageLayout title="Peak Configuration"><Spin /></PageLayout>;
    if (error) return <PageLayout title="Peak Configuration"><Alert type="error" message={error.message} /></PageLayout>;

    return (
        <PageLayout title="Peak Configuration">
            <div className={styles.container}>
                <table className={styles.configTable}>
                    <thead>
                        <tr>
                            <th className={styles.thRow}>Peak</th>
                            <th className={styles.thValue}>Start</th>
                            <th className={styles.thValue}>End</th>
                        </tr>
                    </thead>
                    <tbody>

                        {/* On-Peak Days */}
                        <tr>
                            <td className={styles.fieldCell}>On-Peak Days</td>
                            <td className={styles.valueCell}>
                                <Select
                                    value={onPeakDayStart}
                                    onChange={(v) => { setOnPeakDayStart(v); change(); }}
                                    style={{ width: 150 }}
                                    disabled={isReadOnly}
                                >
                                    {DAY_OPTIONS.map(d => (
                                        <Option key={d.value} value={d.value}>{d.label}</Option>
                                    ))}
                                </Select>
                            </td>
                            <td className={styles.valueCell}>
                                <Select
                                    value={onPeakDayEnd}
                                    onChange={(v) => { setOnPeakDayEnd(v); change(); }}
                                    style={{ width: 150 }}
                                    disabled={isReadOnly}
                                >
                                    {DAY_OPTIONS.map(d => (
                                        <Option key={d.value} value={d.value}>{d.label}</Option>
                                    ))}
                                </Select>
                            </td>
                        </tr>

                        {/* On-Peak Hours */}
                        <tr>
                            <td className={styles.fieldCell}>On-Peak Hours</td>
                            <td className={styles.valueCell}>
                                <Select value={splitTime(onPeakStart)[0]} onChange={(v) => { setOnPeakStart(`${v}:${splitTime(onPeakStart)[1]}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {HOUR_OPTIONS.map(h => <Option key={h} value={h}>{h}</Option>)}
                                </Select>
                                <span style={{ margin: '0 2px' }}>:</span>
                                <Select value={splitTime(onPeakStart)[1]} onChange={(v) => { setOnPeakStart(`${splitTime(onPeakStart)[0]}:${v}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {MINUTE_OPTIONS.map(m => <Option key={m} value={m}>{m}</Option>)}
                                </Select>
                            </td>
                            <td className={styles.valueCell}>
                                <Select value={splitTime(onPeakEnd)[0]} onChange={(v) => { setOnPeakEnd(`${v}:${splitTime(onPeakEnd)[1]}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {HOUR_OPTIONS.map(h => <Option key={h} value={h}>{h}</Option>)}
                                </Select>
                                <span style={{ margin: '0 2px' }}>:</span>
                                <Select value={splitTime(onPeakEnd)[1]} onChange={(v) => { setOnPeakEnd(`${splitTime(onPeakEnd)[0]}:${v}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {MINUTE_OPTIONS.map(m => <Option key={m} value={m}>{m}</Option>)}
                                </Select>
                            </td>
                        </tr>

                        {/* Off-Peak Days */}
                        <tr>
                            <td className={styles.fieldCell}>Off-Peak Days</td>
                            <td className={styles.valueCell}>
                                <Select
                                    value={offPeakDayStart}
                                    onChange={(v) => { setOffPeakDayStart(v); change(); }}
                                    style={{ width: 150 }}
                                    disabled={isReadOnly}
                                >
                                    {DAY_OPTIONS.map(d => (
                                        <Option key={d.value} value={d.value}>{d.label}</Option>
                                    ))}
                                </Select>
                            </td>
                            <td className={styles.valueCell}>
                                <Select
                                    value={offPeakDayEnd}
                                    onChange={(v) => { setOffPeakDayEnd(v); change(); }}
                                    style={{ width: 150 }}
                                    disabled={isReadOnly}
                                >
                                    {DAY_OPTIONS.map(d => (
                                        <Option key={d.value} value={d.value}>{d.label}</Option>
                                    ))}
                                </Select>
                            </td>
                        </tr>

                        {/* Off-Peak Hours */}
                        <tr>
                            <td className={styles.fieldCell}>Off-Peak Hours</td>
                            <td className={styles.valueCell}>
                                <Select value={splitTime(offPeakStart)[0]} onChange={(v) => { setOffPeakStart(`${v}:${splitTime(offPeakStart)[1]}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {HOUR_OPTIONS.map(h => <Option key={h} value={h}>{h}</Option>)}
                                </Select>
                                <span style={{ margin: '0 2px' }}>:</span>
                                <Select value={splitTime(offPeakStart)[1]} onChange={(v) => { setOffPeakStart(`${splitTime(offPeakStart)[0]}:${v}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {MINUTE_OPTIONS.map(m => <Option key={m} value={m}>{m}</Option>)}
                                </Select>
                            </td>
                            <td className={styles.valueCell}>
                                <Select value={splitTime(offPeakEnd)[0]} onChange={(v) => { setOffPeakEnd(`${v}:${splitTime(offPeakEnd)[1]}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {HOUR_OPTIONS.map(h => <Option key={h} value={h}>{h}</Option>)}
                                </Select>
                                <span style={{ margin: '0 2px' }}>:</span>
                                <Select value={splitTime(offPeakEnd)[1]} onChange={(v) => { setOffPeakEnd(`${splitTime(offPeakEnd)[0]}:${v}`); change(); }} style={{ width: 68 }} disabled={isReadOnly}>
                                    {MINUTE_OPTIONS.map(m => <Option key={m} value={m}>{m}</Option>)}
                                </Select>
                            </td>
                        </tr>

                    </tbody>
                </table>

                <div className={styles.actions}>
                    {!isReadOnly && (
                        <NSPButton onClick={handleSave} loading={saving}>
                            Save Configuration
                        </NSPButton>
                    )}
                    {saveStatus === 'saved' && <span className={styles.savedMsg}>Saved successfully.</span>}
                    {saveStatus === 'error' && <span className={styles.errorMsg}>{saveError}</span>}
                </div>
            </div>
        </PageLayout>
    );
};

export default OattPeakConfigPage;
